import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SafeRow from "@/components/SafeRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PlusIcon as Plus, TagIcon as Tag, TrashIcon as Trash, PercentIcon as Percent, CurrencyDollarIcon as CurrencyDollar, BroomIcon as Broom } from "@phosphor-icons/react";
import { formatApiError, formatDate } from "@/lib/utils-app";
import { toast } from "sonner";

const KIND_LABELS = {
  all: "All packages",
  one_time_dog: "Dog reg",
  one_time_litter: "Litter reg",
  plan: "Plan",
  event: "Event",
  certificate: "Certificate",
  breeding_listing: "Breeding listing",
};

export default function AdminPromoCodes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/promo-codes")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleActive = async (id, val) => {
    try {
      await api.patch(`/admin/promo-codes/${id}`, { active: val });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/promo-codes/${id}`);
      toast.success("Promo deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Bulk housekeeping — deletes any code whose `code` starts with
  // T(?)TEST or whose description contains "test"/"smoke". Used to
  // sweep leftover pytest seeds out of the admin view in one click.
  const [clearingSeeds, setClearingSeeds] = useState(false);
  const clearTestSeeds = async () => {
    setClearingSeeds(true);
    try {
      const r = await api.post("/admin/promo-codes/clear-test-seeds");
      const { deleted, codes } = r.data || {};
      if (!deleted) {
        toast.info("No test seeds found", {
          description: "Nothing matched the TTEST/TEST/“test”/“smoke” pattern.",
        });
      } else {
        toast.success(`Cleared ${deleted} test seed${deleted === 1 ? "" : "s"}`, {
          description: codes.slice(0, 6).join(", ") + (codes.length > 6 ? `, +${codes.length - 6} more` : ""),
        });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClearingSeeds(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Admin</p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Promo codes</h1>
          <p className="text-[#6B6B6B] mt-2 max-w-2xl">
            Create discount codes for special occasions, members, or events. Codes apply at
            checkout across registrations, plans, certificates, breeding listings, and event entries.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* "Clear test seeds" is intentionally a secondary, almost-
              quiet action. We compute the visible count locally so we
              can show "(n)" and hide the button entirely when there's
              nothing to clear — the admin shouldn't have to remember a
              dangerous pattern, the UI should be self-evident. */}
          {(() => {
            // Match the backend regex exactly:
            //   - Original "TTEST/TEST" prefix
            //   - 2+ letters followed by 6+ hex chars (pytest fixture)
            //   - "test"/"smoke" in description
            const testCount = rows.filter((r) =>
              /^T?TEST/i.test(r.code) ||
              /^[A-Z]{2,}[0-9A-F]{6,}$/.test(r.code) ||
              /\b(test|smoke)\b/i.test(r.description || "")
            ).length;
            if (!testCount) return null;
            return (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full border-[#E5E5E5] text-[#6B6B6B] hover:text-crimson hover:border-crimson/40"
                    data-testid="clear-test-seeds-btn"
                    disabled={clearingSeeds}
                  >
                    <Broom size={14} className="mr-1.5" />
                    {clearingSeeds ? "Clearing…" : `Clear test seeds (${testCount})`}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear {testCount} test seed{testCount === 1 ? "" : "s"}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Deletes every promo whose code starts with <code>TTEST</code>/<code>TEST</code>
                      &nbsp;or whose description contains "test" / "smoke".
                      Any past redemptions stay in the audit log.
                      <br /><br />
                      This is a one-shot housekeeping action and cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={clearTestSeeds}
                      data-testid="clear-test-seeds-confirm"
                    >
                      Clear seeds
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            );
          })()}
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="new-promo-btn"
          >
            <Plus size={14} className="mr-1" /> New code
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-16 text-center" data-testid="promo-empty">
          <Tag size={48} weight="duotone" className="text-crimson mx-auto mb-4" />
          <h2 className="font-spectral text-2xl tracking-tight">No promo codes yet</h2>
          <p className="text-[#6B6B6B] mt-2 max-w-md mx-auto">
            Tap "New code" to create your first discount.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="promo-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#FAFAFA] text-[#6B6B6B]">
              <tr>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.15em] text-xs">Code</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.15em] text-xs">Discount</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.15em] text-xs">Applies to</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.15em] text-xs">Uses</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.15em] text-xs">Expires</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.15em] text-xs">Active</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]">
              {rows.map((r) => (
                <SafeRow key={r.id} rowId={r.code || r.id} colSpan={7}>
                <tr data-testid={`promo-row-${r.code}`}>
                  <td className="px-4 py-3 font-mono font-bold tracking-wide">{r.code}</td>
                  <td className="px-4 py-3">
                    {r.kind === "percent" ? (
                      <span className="inline-flex items-center gap-1">
                        <Percent size={14} className="text-crimson" /> {Number(r.value ?? 0)}% off
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <CurrencyDollar size={14} className="text-crimson" /> {Number(r.value ?? 0).toFixed(2)} off
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.applies_to || []).map((k) => (
                        <span key={k} className="text-[10px] bg-[#FAFAFA] border border-[#E5E5E5] rounded-full px-2 py-0.5 text-[#6B6B6B]">
                          {KIND_LABELS[k] || k}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#6B6B6B] text-xs">
                    {r.uses}{r.max_uses ? ` / ${r.max_uses}` : ""}
                  </td>
                  <td className="px-4 py-3 text-[#6B6B6B] text-xs">
                    {r.expires_at ? formatDate(r.expires_at) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={r.active}
                      onCheckedChange={(v) => toggleActive(r.id, v)}
                      data-testid={`promo-toggle-${r.code}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-1.5 rounded-full hover:bg-[#C1272D]/10 text-[#6B6B6B] hover:text-crimson transition-colors" data-testid={`promo-delete-${r.code}`}>
                          <Trash size={16} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete code {r.code}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Anyone using this code at checkout will get an error. Past redemptions
                            are kept in the audit log.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(r.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
                </SafeRow>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <CreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          load();
        }}
      />
    </AppShell>
  );
}

function CreateDialog({ open, onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [appliesAll, setAppliesAll] = useState(true);
  const [appliesTo, setAppliesTo] = useState([]);
  const [maxUses, setMaxUses] = useState("0");
  const [perUserLimit, setPerUserLimit] = useState("1");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const KINDS = [
    "one_time_dog", "one_time_litter", "plan", "event", "certificate", "breeding_listing",
  ];

  const reset = () => {
    setCode(""); setDescription(""); setKind("percent"); setValue("");
    setAppliesAll(true); setAppliesTo([]); setMaxUses("0"); setPerUserLimit("1");
    setExpiresAt("");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/admin/promo-codes", {
        code,
        description,
        kind,
        value: parseFloat(value),
        applies_to: appliesAll ? ["all"] : appliesTo,
        max_uses: parseInt(maxUses) || 0,
        per_user_limit: parseInt(perUserLimit) || 0,
        expires_at: expiresAt || null,
      });
      toast.success(`Code ${code.toUpperCase()} created`);
      reset();
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New promo code</DialogTitle>
          <DialogDescription>
            Create a discount code redeemable at checkout. Set a percent or fixed amount,
            scope it to specific package kinds, and optionally cap usage per code or per user.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Code</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="WELCOME25"
              className="mt-1 rounded-lg uppercase font-mono"
              data-testid="promo-create-code"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Internal note (optional)"
              className="mt-1 rounded-lg"
              data-testid="promo-create-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Type</Label>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="mt-1 w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="promo-create-kind"
              >
                <option value="percent">Percent off</option>
                <option value="fixed">Fixed amount off (USD)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                {kind === "percent" ? "% off" : "$ off"}
              </Label>
              <Input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                min="0"
                step="0.01"
                className="mt-1 rounded-lg"
                data-testid="promo-create-value"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 block">Applies to</Label>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={appliesAll}
                onChange={(e) => setAppliesAll(e.target.checked)}
                data-testid="promo-applies-all"
              />
              <span className="text-sm">All packages</span>
            </label>
            {!appliesAll && (
              <div className="flex flex-wrap gap-2">
                {KINDS.map((k) => (
                  <label key={k} className="inline-flex items-center gap-1.5 text-xs border border-[#E5E5E5] rounded-full px-3 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={appliesTo.includes(k)}
                      onChange={(e) => {
                        setAppliesTo((prev) =>
                          e.target.checked ? [...prev, k] : prev.filter((x) => x !== k)
                        );
                      }}
                    />
                    {KIND_LABELS[k]}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Max uses</Label>
              <Input
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                min="0"
                className="mt-1 rounded-lg"
                placeholder="0 = ∞"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Per user</Label>
              <Input
                type="number"
                value={perUserLimit}
                onChange={(e) => setPerUserLimit(e.target.value)}
                min="0"
                className="mt-1 rounded-lg"
                placeholder="0 = ∞"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Expires</Label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="mt-1 rounded-lg"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !code || !value}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="promo-create-submit"
          >
            {submitting ? "Creating…" : "Create code"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
